const SHA256_PATTERN = /^sha256:[0-9a-f]{64}$/;
export const OFFLINE_EXPORT_CSP = "default-src 'none'; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'; frame-ancestors 'none'";

export class ExportIntegrityError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ExportIntegrityError";
  }
}

export function hasExactMediaType(value: string, expected: string): boolean {
  const [mediaType] = value.split(";", 1);
  return mediaType?.trim().toLocaleLowerCase() === expected.toLocaleLowerCase();
}

export async function readBoundedResponseBytes(response: Response, maximumBytes: number): Promise<Uint8Array<ArrayBuffer>> {
  const declaredLength = response.headers.get("Content-Length");
  if (declaredLength !== null) {
    const parsedLength = Number(declaredLength);
    if (!Number.isSafeInteger(parsedLength) || parsedLength < 0) {
      throw new ExportIntegrityError("response Content-Length is invalid");
    }
    if (parsedLength > maximumBytes) {
      throw new ExportIntegrityError(`response body exceeds ${maximumBytes} bytes`);
    }
  }

  if (!response.body) {
    const value = new Uint8Array(await response.arrayBuffer());
    if (value.byteLength > maximumBytes) throw new ExportIntegrityError(`response body exceeds ${maximumBytes} bytes`);
    return value;
  }

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let totalBytes = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      totalBytes += value.byteLength;
      if (totalBytes > maximumBytes) {
        await reader.cancel("bounded export response exceeded its byte limit");
        throw new ExportIntegrityError(`response body exceeds ${maximumBytes} bytes`);
      }
      chunks.push(value);
    }
  } finally {
    reader.releaseLock();
  }

  const result = new Uint8Array(totalBytes);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return result;
}

export async function sha256Identity(bytes: Uint8Array): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return `sha256:${Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, "0")).join("")}`;
}

export async function verifyJsonExportBytes(bytes: Uint8Array, expectedDigest: string): Promise<void> {
  if (!SHA256_PATTERN.test(expectedDigest)) throw new ExportIntegrityError("expected semantic digest is invalid");
  if (await sha256Identity(bytes) !== expectedDigest) {
    throw new ExportIntegrityError("JSON export bytes do not match the displayed semantic digest");
  }
}

export function verifyOfflineHtmlBytes(bytes: Uint8Array, expectedMarkers: readonly string[]): void {
  let markup: string;
  try {
    markup = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    throw new ExportIntegrityError("HTML export is not valid UTF-8");
  }
  if (!markup.toLocaleLowerCase().startsWith("<!doctype html>")) {
    throw new ExportIntegrityError("HTML export is missing the offline document marker");
  }
  const lowerMarkup = markup.toLocaleLowerCase();
  const headStart = lowerMarkup.indexOf("<head");
  const headEnd = lowerMarkup.indexOf("</head>");
  const bodyStart = lowerMarkup.indexOf("<body");
  if (headStart < 0 || headEnd <= headStart || bodyStart <= headEnd) {
    throw new ExportIntegrityError("HTML export is missing a closed head before its body");
  }
  const head = markup.slice(headStart, headEnd);
  const requiredCsp = `<meta http-equiv="Content-Security-Policy" content="${OFFLINE_EXPORT_CSP}">`;
  if (!head.includes(requiredCsp)) {
    throw new ExportIntegrityError("HTML export is missing the frozen offline Content-Security-Policy");
  }
  if (/<\s*(?:script|iframe|object|embed|link|base|form)\b/i.test(markup)
    || /<meta\b[^>]*http-equiv\s*=\s*["']?refresh\b/i.test(markup)
    || /\son[a-z0-9_-]+\s*=/i.test(markup)
    || /\s(?:src|href)\s*=\s*["']/i.test(markup)) {
    throw new ExportIntegrityError("HTML export contains an active or external content surface");
  }
  const inertMarkup = markup
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<style\b[\s\S]*?<\/style>/gi, "");
  for (const marker of expectedMarkers) {
    if (!marker || !inertMarkup.includes(marker)) {
      throw new ExportIntegrityError("HTML export does not bind the displayed digest and replay selector");
    }
  }
}

export function exportBlob(bytes: Uint8Array<ArrayBuffer>, mediaType: string): Blob {
  return new Blob([bytes], { type: mediaType });
}
